import React, {
  forwardRef,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import {
  BodyScrollEndEvent,
  ColDef,
  ColumnResizedEvent,
  GridOptions,
} from 'ag-grid-community/dist/lib/main';
import TablePlaceholderSvg from '@/src/assets/icons/icon-table-placeholder.svg';
import { useSearchParams } from 'react-router-dom';
import { PulseLoader } from 'react-spinners';
import colorSet from '@/src/styles/color';
import LocalIcon from '../Icons/LocalIcon';
import Typo from '../Typo';
import PaginationWithPageSize from '../../molecule/PaginationWithPageSize';
import { cn, createDebouncer } from '@/src/utils';

export interface TableProps extends GridOptions {
  rowData: GridOptions['rowData'];
  columnDefs: ColDef[];
  total?: number;
  page?: number;
  pageSize?: number;
  isPagination?: boolean;
  isTotal?: boolean;
  handlePaginationClick?: (page: number, pageSize: number) => void;
  onSortChange?: (value?: {
    sort: string;
    order: 'ASC' | 'DESC' | null | undefined;
  }) => void;
  isLoading?: boolean;
  isAutoHeight?: boolean;
  isPaginationDataMapping?: boolean;
  containerClassName?: string;
  tableClassName?: string;
  isSelectToRadio?: boolean;
}

export const renderNoRowsComponent = (text?: string) => {
  return (
    <div className="flex flex-col items-center justify-center gap-[4px]">
      <LocalIcon iconSrc={TablePlaceholderSvg} iconSize={56} />
      <Typo typoColor="gray2" typoType="b14r">
        {text ? text : '데이터가 없습니다'}
      </Typo>
    </div>
  );
};

const Table = forwardRef<AgGridReact, TableProps>(
  (
    {
      rowData,
      columnDefs,
      total = 0,
      page = 1,
      pageSize = 10,
      isPagination = true,
      isTotal = false,
      handlePaginationClick,
      onSortChange,
      isAutoHeight = true,
      isPaginationDataMapping = false,
      containerClassName,
      tableClassName,
      isSelectToRadio = true,
      ...props
    },
    ref,
  ) => {
    const gridRef = useRef<AgGridReact>();
    const [searchParams] = useSearchParams();
    const [sortWithColumnDefs, setSortWithColumnDefs] = useState(columnDefs);
    const [bodyWidth, setBodyWidth] = useState(0);
    const [shadowClass, setShadowClass] = useState('');

    const sort = searchParams.get('sort');
    const order = searchParams.get('order');

    const isPaginationMaping =
      isPaginationDataMapping && page && pageSize && isPagination;
    // 첫 렌더링시 Sort 여부에 따른 ColumnDefs 변환
    useLayoutEffect(() => {
      const sortColumnDefs = columnDefs.map(item => {
        if (sort === item.field) {
          return { ...item, sort: order?.toLocaleLowerCase() };
        }

        return { ...item };
      }) as ColDef[];

      setSortWithColumnDefs(sortColumnDefs);
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      window.addEventListener('error', e => {
        if (
          e.message === 'ResizeObserver loop limit exceeded' ||
          e.message ===
            'ResizeObserver loop completed with undelivered notifications.'
        ) {
          const resizeObserverErrDiv = document.getElementById(
            'webpack-dev-server-client-overlay-div',
          );
          const resizeObserverErr = document.getElementById(
            'webpack-dev-server-client-overlay',
          );
          if (resizeObserverErr) {
            resizeObserverErr.setAttribute('style', 'display: none');
          }
          if (resizeObserverErrDiv) {
            resizeObserverErrDiv.setAttribute('style', 'display: none');
          }
        }
      });
    }, []);

    const defaultColDef = useMemo(
      () => ({
        resizable: true,
        sortable: false,
        comparator: () => 0,
      }),
      [],
    );

    const debounceFunc = createDebouncer(300);

    const handleScrollShadow = useCallback(
      (scrollLeft: number, scrollRight: number, bodyWidth: number) => {
        if (scrollLeft > 0 && scrollRight < bodyWidth) {
          setShadowClass('shadow-both');
        } else if (scrollLeft === 0 && scrollRight < bodyWidth) {
          setShadowClass('shadow-right');
        } else if (scrollLeft > 0 && scrollRight === bodyWidth) {
          setShadowClass('shadow-left');
        } else {
          setShadowClass('');
        }
      },
      [],
    );

    const bodyScrollEndHandler = useCallback(
      (e: BodyScrollEndEvent) => {
        const { left, right } = e.api.getHorizontalPixelRange() || {
          left: 0,
          right: 0,
        };

        handleScrollShadow(left, right, bodyWidth);
      },
      [handleScrollShadow, bodyWidth],
    );

    const columnResizeHandler = useCallback(
      (e: ColumnResizedEvent) => {
        debounceFunc(() => {
          const { left, right } = e.api.getHorizontalPixelRange() || {
            left: 0,
            right: 0,
          };

          const sumWidth = e.columnApi.getColumns()?.reduce((acc, cur) => {
            if (!cur?.isPinned()) {
              return acc + cur.getActualWidth();
            }
            return acc;
          }, 0);

          handleScrollShadow(left, right, sumWidth ?? 0);
          setBodyWidth(sumWidth ?? 0);
        });
      },
      [debounceFunc, handleScrollShadow],
    );

    const renderLoadingComponent = () => {
      return <PulseLoader color={colorSet.blue4} />;
    };

    return (
      <div
        className={cn(
          'ag-theme-alpine',
          shadowClass,
          containerClassName,
          isSelectToRadio && 'ag-radio-theme',
        )}
      >
        <AgGridReact
          className={tableClassName}
          ref={node => {
            if (node) {
              gridRef.current = node;
            }
            if (ref) {
              if (typeof ref === 'function') {
                ref(node);
              } else {
                ref.current = node;
              }
            }
          }}
          enableCellTextSelection
          onBodyScrollEnd={bodyScrollEndHandler}
          onColumnResized={columnResizeHandler}
          pagination={isPagination}
          suppressPaginationPanel={true}
          paginationPageSize={pageSize}
          rowData={rowData?.map(row => {
            if (isPaginationMaping) {
              return { ...row, page, pageSize };
            }

            return { ...row };
          })}
          suppressDragLeaveHidesColumns
          domLayout={isAutoHeight ? 'autoHeight' : 'normal'}
          columnDefs={sortWithColumnDefs}
          defaultColDef={defaultColDef}
          alwaysShowHorizontalScroll={false}
          noRowsOverlayComponent={() => renderNoRowsComponent()}
          loadingOverlayComponent={() => renderLoadingComponent()}
          onSortChanged={e => {
            const allColumns = e.columnApi.getAllGridColumns();
            const sortModel = allColumns
              .map(column => ({
                sort: column.getColId(),
                order: column.getSort()?.toUpperCase(),
              }))
              .filter(item => !!item.order);

            // props로 받은 이벤트 함수
            onSortChange?.(sortModel?.[0] as any);
          }}
          unSortIcon
          {...props}
        />

        {!isPagination && isTotal && (
          <div className="relative flex h-[56px] items-center justify-between pt-4">
            <Typo typoType="b14r">Total {total}</Typo>
          </div>
        )}
        {isPagination && rowData && rowData?.length > 0 && (
          <PaginationWithPageSize
            total={total}
            page={page}
            pageSize={pageSize}
            handlePaginationClick={(page, pageSize) => {
              handlePaginationClick && handlePaginationClick(page, pageSize);
            }}
          />
        )}
      </div>
    );
  },
);

export default Table;
